/**
 * 空间应用详情侧栏：应用信息、管理员（TagGroup）、可见范围 Switch + TagGroup
 */
import {
  AlertDialog,
  Avatar,
  Button,
  Description,
  Switch,
  Tag,
  TagGroup,
  toast,
} from '@heroui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sheet } from '@heroui-pro/react';
import {
  IconPlus as Plus,
  IconUserPlus as UserPlus,
} from '@tabler/icons-react';
import { useAppSelector } from '@/stores/hooks';
import { trpc } from '@/lib/trpc';
import { AppIcon } from '@/components/AppIcon';
import { ContactVisibilityRulesSettings } from './ContactVisibilityRulesSettings';
import { AddPermissionUserModal } from './AddPermissionUserModal';
import { AddVisibilityPickerModal } from './AddVisibilityPickerModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { selectionKey } from './visibility-utils';

type VisibilityRuleWithTarget = {
  targetType?: string | null;
  targetId?: string | null;
  label?: string | null;
  avatarUrl?: string | null;
  org?: { shortName?: string | null; fullName?: string | null } | null;
  person?: { fullName?: string | null; avatarUrl?: string | null } | null;
  user?: { nickname?: string | null; username?: string | null; avatarUrl?: string | null } | null;
  role?: { name?: string | null; code?: string | null } | null;
};

function resolveVisibilityDisplayLabel(rule: VisibilityRuleWithTarget): string {
  switch (rule.targetType) {
    case 'ORG':
      return rule.org?.shortName || rule.org?.fullName || rule.label || '—';
    case 'PEOPLE':
      return rule.person?.fullName || rule.label || '—';
    case 'USER':
      return rule.user?.nickname || rule.user?.username || rule.label || '—';
    case 'ROLE':
      return rule.role?.name || rule.role?.code || rule.label || '—';
    default:
      return rule.label || rule.targetType || '—';
  }
}

export interface SpaceAppDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  spaceAppId: string;
  appId: string;
  /** 应用编码，用于加载应用专属设置面板 */
  appCode?: string | null;
  isPublic?: boolean;
  summary: {
    name: string;
    description?: string | null;
    icon?: string | null;
  };
}

type AppAdminItem = {
  id: string;
  username?: string | null;
  nickname?: string | null;
  avatarUrl?: string | null;
};

type VisibilityTagItem = {
  id: string;
  key: string;
  label: string;
  avatarUrl?: string | null;
};

type TagListItem = {
  id: string;
  label: string;
  avatarUrl?: string | null;
};

function RemovableTagGroup({
  items,
  emptyText,
  isDisabled,
  onRemove,
  'aria-label': ariaLabel,
}: {
  items: TagListItem[];
  emptyText?: string;
  isDisabled?: boolean;
  onRemove: (id: string) => void;
  'aria-label': string;
}) {
  if (items.length === 0 && emptyText) {
    return <Description className="text-sm">{emptyText}</Description>;
  }

  const handleRemove = (id: string) => {
    onRemove(id);
  };

  return (
    <TagGroup size="lg" aria-label={ariaLabel}>
      <TagGroup.List
        items={items}
        renderEmptyState={() =>
          emptyText ? <Description className="p-1 text-sm">{emptyText}</Description> : null
        }
      >
        {(item) => (
          <Tag id={item.id} textValue={item.label}>
            <Avatar className="size-5 shrink-0" variant="soft">
              {item.avatarUrl ? <Avatar.Image src={item.avatarUrl} alt={item.label} /> : null}
              <Avatar.Fallback className="text-xs">
                {item.label.charAt(0).toUpperCase()}
              </Avatar.Fallback>
            </Avatar>
            {item.label}
            {!isDisabled ? <Tag.RemoveButton onPress={() => handleRemove(item.id)} /> : null}
          </Tag>
        )}
      </TagGroup.List>
    </TagGroup>
  );
}

export function SpaceAppDetailSheet({
  open,
  onOpenChange,
  spaceAppId,
  appId,
  appCode,
  isPublic: isPublicProp = false,
  summary,
}: SpaceAppDetailSheetProps) {
  const user = useAppSelector((state) => state.auth.user);
  const spaceId = user?.spaces?.[0]?.id;

  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [addVisibilityOpen, setAddVisibilityOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VisibilityTagItem | null>(null);

  const utils = trpc.useUtils();

  const { data: spaceAppsList, isLoading: spaceAppLoading } = trpc.spaceApps.listAll.useQuery(
    { spaceId: spaceId! },
    { enabled: open && !!spaceId }
  );

  const spaceAppRow = useMemo(
    () => spaceAppsList?.find((row) => row.id === spaceAppId),
    [spaceAppsList, spaceAppId]
  );

  const isPublic = spaceAppRow?.isPublic ?? isPublicProp;

  const admins = useMemo(() => {
    const raw = (spaceAppRow as { admin?: AppAdminItem[] | null } | undefined)?.admin;
    return Array.isArray(raw) ? raw : [];
  }, [spaceAppRow]);

  const { data: visibilityRules, isLoading: visibilityLoading } =
    trpc.spaceAppVisibility.listAllWithLabels.useQuery(
      { appId },
      { enabled: open && !!appId && !isPublic }
    );

  const updateSpaceAppMutation = trpc.spaceApps.update.useMutation({
    onSuccess: () => {
      if (spaceId) void utils.spaceApps.listAll.invalidate({ spaceId });
    },
    onError: (e) => toast.danger(e.message || '更新失败'),
  });

  const removeAdminsMutation = trpc.spaceApps.removeAdmins.useMutation({
    onSuccess: () => {
      toast.success('管理员已更新');
      if (spaceId) void utils.spaceApps.listAll.invalidate({ spaceId });
    },
    onError: (e) => toast.danger(e.message || '更新失败'),
  });

  const deleteMutation = trpc.spaceAppVisibility.delete.useMutation({
    onSuccess: () => {
      toast.success('已移除可见性规则');
      setDeleteTarget(null);
      void utils.spaceAppVisibility.listAllWithLabels.invalidate({ appId });
    },
    onError: (e) => toast.danger(e.message || '删除失败'),
  });

  useEffect(() => {
    if (!open) {
      setAddAdminOpen(false);
      setAddVisibilityOpen(false);
      setDeleteTarget(null);
    }
  }, [open]);

  const handleSheetOpenChange = useCallback(
    (next: boolean) => {
      if (!next) onOpenChange(false);
    },
    [onOpenChange]
  );

  const displayAdminName = useCallback((admin: AppAdminItem) => {
    return admin.nickname || admin.username || `用户 ${admin.id}`;
  }, []);

  const handleRemoveAdmin = useCallback(
    (userId: string) => {
      removeAdminsMutation.mutate({ appId, userIds: [userId] });
    },
    [appId, removeAdminsMutation]
  );

  const handleIsPublicChange = useCallback(
    (checked: boolean) => {
      updateSpaceAppMutation.mutate({
        id: spaceAppId,
        data: { isPublic: checked },
      });
    },
    [spaceAppId, updateSpaceAppMutation]
  );

  const visibilityTags = useMemo<VisibilityTagItem[]>(() => {
    return (visibilityRules ?? [])
      .filter((rule): rule is typeof rule & { id: string } => rule.id != null)
      .map((rule) => ({
        id: rule.id,
        key: selectionKey(rule.targetType ?? 'USER', rule.targetId ?? rule.id),
        label: resolveVisibilityDisplayLabel(rule),
        avatarUrl: rule.avatarUrl ?? rule.person?.avatarUrl ?? rule.user?.avatarUrl ?? null,
      }));
  }, [visibilityRules]);

  const existingVisibilityKeys = useMemo(
    () => new Set(visibilityTags.map((t) => t.key)),
    [visibilityTags]
  );

  const adminUserIds = useMemo(() => admins.map((a) => a.id), [admins]);

  const invalidateAll = useCallback(() => {
    void utils.spaceAppVisibility.listAllWithLabels.invalidate({ appId });
    if (spaceId) void utils.spaceApps.listAll.invalidate({ spaceId });
  }, [appId, spaceId, utils.spaceAppVisibility.listAllWithLabels, utils.spaceApps.listAll]);

  const adminTagItems = useMemo<TagListItem[]>(
    () =>
      admins.map((admin) => ({
        id: admin.id,
        label: displayAdminName(admin),
        avatarUrl: admin.avatarUrl,
      })),
    [admins, displayAdminName]
  );

  const visibilityTagItems = useMemo<TagListItem[]>(
    () =>
      visibilityTags.map((tag) => ({
        id: tag.id,
        label: tag.label,
        avatarUrl: tag.avatarUrl,
      })),
    [visibilityTags]
  );

  const handleRemoveVisibilityTag = useCallback(
    (id: string) => {
      const target = visibilityTags.find((t) => t.id === id);
      if (target) setDeleteTarget(target);
    },
    [visibilityTags]
  );

  return (
    <>
      <Sheet isOpen={open} placement="right" onOpenChange={handleSheetOpenChange}>
        <Sheet.Backdrop>
          <Sheet.Content className="w-[min(720px,90vw)]">
            <Sheet.Dialog className="flex h-[100dvh] flex-col">
              <Sheet.Header className="shrink-0 gap-2">
                <Sheet.Heading className="text-xl font-medium">应用详情</Sheet.Heading>
              </Sheet.Header>
              <Sheet.Body className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto p-4">
                <div className="flex shrink-0 gap-4">
                  <div className="bg-surface-secondary text-accent flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                    <AppIcon icon={summary.icon} className="size-full object-contain p-1.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-semibold leading-tight">{summary.name}</h2>
                    {summary.description ? (
                      <Description className="mt-1 text-sm">{summary.description}</Description>
                    ) : (
                      <Description className="text-muted-foreground mt-1 text-sm">应用描述</Description>
                    )}
                  </div>
                </div>

                <section className="flex shrink-0 flex-col gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-base font-semibold">管理员</h3>
                    <Button size="sm" variant="primary" onPress={() => setAddAdminOpen(true)}>
                      <UserPlus className="size-4" stroke={1.5} />
                      添加管理员
                    </Button>
                  </div>
                  {spaceAppLoading ? (
                    <p className="text-muted-foreground text-sm">加载管理员…</p>
                  ) : (
                    <RemovableTagGroup
                      aria-label="应用管理员"
                      items={adminTagItems}
                      emptyText="暂无管理员"
                      isDisabled={removeAdminsMutation.isPending}
                      onRemove={handleRemoveAdmin}
                    />
                  )}
                </section>

                <section className="flex shrink-0 flex-col gap-3">
                  <h3 className="text-base font-semibold">可见范围</h3>
                  <div className="flex h-9 min-h-9 items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Switch
                        isSelected={!!isPublic}
                        isDisabled={updateSpaceAppMutation.isPending}
                        onChange={handleIsPublicChange}
                      >
                        <Switch.Content>
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Content>
                      </Switch>
                      <span className="text-sm">{isPublic ? '全员可见' : '部分可见'}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      className={isPublic ? 'pointer-events-none invisible' : undefined}
                      isDisabled={isPublic || updateSpaceAppMutation.isPending}
                      onPress={() => setAddVisibilityOpen(true)}
                    >
                      <Plus className="size-4" stroke={1.5} />
                      添加用户
                    </Button>
                  </div>
                  {!isPublic ? (
                    visibilityLoading ? (
                      <p className="text-muted-foreground text-sm">加载可见范围…</p>
                    ) : (
                      <RemovableTagGroup
                        aria-label="可见范围"
                        items={visibilityTagItems}
                        emptyText="点击添加用户按钮配置可见范围"
                        onRemove={handleRemoveVisibilityTag}
                      />
                    )
                  ) : null}
                </section>

                {appCode === 'contacts' && spaceId ? (
                  <ContactVisibilityRulesSettings appId={appId} spaceId={spaceId} />
                ) : null}
              </Sheet.Body>
            </Sheet.Dialog>
          </Sheet.Content>
        </Sheet.Backdrop>
      </Sheet>

      <AddPermissionUserModal
        open={addAdminOpen}
        onOpenChange={setAddAdminOpen}
        appId={appId}
        spaceAppId={spaceAppId}
        mode="admin"
        existingAdminUserIds={adminUserIds}
        onSuccess={invalidateAll}
      />

      {spaceId ? (
        <AddVisibilityPickerModal
          open={addVisibilityOpen}
          onOpenChange={setAddVisibilityOpen}
          appId={appId}
          spaceId={spaceId}
          existingKeys={existingVisibilityKeys}
          onSuccess={invalidateAll}
        />
      ) : null}

      <AlertDialog isOpen={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>确定移除「{deleteTarget?.label}」的可见性规则吗？</AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setDeleteTarget(null)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending || !deleteTarget}
                  onPress={() => {
                    if (!deleteTarget) return;
                    deleteMutation.mutate({ id: deleteTarget.id });
                  }}
                >
                  {deleteMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
